import { useTranslation } from 'react-i18next';
import type { CoreOfficeDataType } from '~/types/offices';
import { Button } from '~/components/common/buttons/Button';
import SVGIcon from '~/components/ui/Icons/SVGIcon';
import ValidatedFormWrapper from '~/components/common/forms/ValidatedFormWrapper';
import { contactRequestSchema } from '~/validations/contactRequestSchema';
import { useControlField } from 'remix-validated-form';
import { useEffect, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { useSearchParams } from '@remix-run/react';
import { useUser } from '~/modules/useUser';
import PricePerSeatBreakdown from './PricePerSeatBreakDown';
import useWindowDimensions from '~/hooks/useWindowDimensions';
import RequestDatepickerDropdown from '~/components/pages/office/RequestDatepickerDropdown';
import mixpanelTrack from '~/hooks/useMixPanel';
import { useLanguageParam } from '~/config/i18n';
import { HostDataType } from '~/types/userTypes';
import { ga4TrackInitiateContactRequest } from '~/hooks/useGoogleTagManager';
import FakeAddonInput from '~/components/common/forms/FakeAddonInput';
import SeatsAndFlexibility from '~/components/common/forms/SeatsAndFlexibility';
import { findCapacity } from '../request/utils';
import PriceBreakdown from './PriceBreakdown';
import mixpanelSSTrack from '~/hooks/useMixpanelSS';
import RequestDatepickerModal from './RequestDatepickerModal';

declare var bootstrap: any;

type TProps = {
  office: CoreOfficeDataType;
  hostData: HostDataType;
};

const RequestVisitComponent = ({ office, hostData }: TProps) => {
  const [moveinDate, setMoveinDate] = useControlField<DateTime | null>('moveinDate', 'requestVisitForm');
  const [moveoutDate, setMoveoutDate] = useControlField<DateTime | null>('moveoutDate', 'requestVisitForm');
  const [requiredCapacity, setRequiredCapacity] = useState<string>(findCapacity(office).toString());
  const [daysPerWeek, setDaysPerWeek] = useState<string>(office.highestFlexibility.toString());
  const [openDatepickerDropdown, setOpenDatePickerDropdown] = useState(false);

  const requestDatePickerModalRef = useRef(null);

  const { t } = useTranslation();
  const clientStore = useUser();

  const [searchParams] = useSearchParams();
  const requiredCapacityParam = searchParams.get('requiredCapacity');

  const { width } = useWindowDimensions() || 769;

  const lang = useLanguageParam();

  const handleDatePicker = () => {
    if (width && width > 768) {
      setOpenDatePickerDropdown(true);
    } else {
      const requestDatePickerModalElement = requestDatePickerModalRef.current;
      if (requestDatePickerModalElement) {
        const modalInstance = bootstrap.Modal.getOrCreateInstance(requestDatePickerModalElement);
        modalInstance.show();
      }
    }
  };

  const handleSubmit = () => {
    mixpanelTrack('E.contact_intent');
    mixpanelSSTrack('E.contact_intent_sst');
    ga4TrackInitiateContactRequest(lang, office, hostData);
  };

  useEffect(() => {
    //set to capacity from params if brought over from search
    if (
      requiredCapacityParam &&
      office.minCapacity &&
      parseInt(requiredCapacityParam) >= office.minCapacity &&
      parseInt(requiredCapacityParam) <= office.capacity
    ) {
      setRequiredCapacity(requiredCapacityParam);
      return;
    }
  }, []);

  return (
    <>
      <ValidatedFormWrapper
        actionPath={`/contact-request-submit/${office.id}?lang=${lang}`}
        formSchema={contactRequestSchema}
        formId="requestVisitForm"
        onSubmit={handleSubmit}
      >
        <PricePerSeatBreakdown
          daysPerWeek={parseInt(daysPerWeek)}
          requiredCapacity={parseInt(requiredCapacity)}
          office={office}
          startDate={moveinDate}
          endDate={moveoutDate}
        />
        <div className="position-relative">
          {openDatepickerDropdown && (
            <RequestDatepickerDropdown
              office={office}
              selectedStartDate={moveinDate?.toJSDate() || undefined}
              selectedEndDate={moveoutDate?.toJSDate() || undefined}
              close={() => {
                setOpenDatePickerDropdown(false);
              }}
              clear={() => {
                setMoveinDate(null);
                setMoveoutDate(null);
              }}
              apply={(start, end) => {
                // console.log('start=', start, ' end=', end);
                setMoveinDate(DateTime.fromJSDate(start));
                setMoveoutDate(DateTime.fromJSDate(end));
              }}
            />
          )}
        </div>
        <FakeAddonInput
          label={t('office.show.requestVisitCard.dateInput.label')}
          placeHolder={t('office.show.requestVisitCard.dateInput.placeHolder')}
          onClick={() => handleDatePicker()}
          extraClasses="mt-4 mt-md-3 pe-2"
          value={
            moveinDate === null || moveoutDate === null || moveinDate === undefined || moveoutDate === undefined
              ? null
              : `${moveinDate.toFormat('dd/MM/yyyy')} - ${moveoutDate.toFormat('dd/MM/yyyy')}`
          }
        />
        <input
          type="hidden"
          name={'moveinDate'}
          value={moveinDate?.toISODate() || ''}
        />
        <input
          type="hidden"
          name={'moveoutDate'}
          value={moveoutDate?.toISODate() || ''}
        />

        <SeatsAndFlexibility
          office={office}
          requiredCapacity={requiredCapacity}
          daysPerWeek={daysPerWeek}
          setRequiredCapacity={setRequiredCapacity}
          setDaysPerWeek={setDaysPerWeek}
        />

        <Button
          disabled={clientStore?.isHost || office.publicationState === 'unpublished'}
          type="submit"
          variant="accent"
          extraClasses="w-100 mt-4 mt-md-3"
          size="lg"
        >
          <span className="text-1 -medium me-2">{t('office.show.requestVisitCard.submitBtn')}</span>
          <SVGIcon iconType="long-arrow-right" />
        </Button>

        <div className="mt-3">
          <PriceBreakdown
            office={office}
            moveinDate={moveinDate}
            moveoutDate={moveoutDate}
            requiredCapacity={requiredCapacity !== '' ? parseInt(requiredCapacity) : null}
            daysPerWeek={daysPerWeek !== '' ? parseInt(daysPerWeek) : null}
            extraClasses="mt-3 pt-3 fo-border-top-sm border-grey-200"
          />
        </div>
      </ValidatedFormWrapper>
      <RequestDatepickerModal
        modalRef={requestDatePickerModalRef}
        office={office}
        selectedStartDate={moveinDate?.toJSDate() || undefined}
        selectedEndDate={moveoutDate?.toJSDate() || undefined}
        clear={() => {
          setMoveinDate(null);
          setMoveoutDate(null);
        }}
        apply={(start, end) => {
          setMoveinDate(DateTime.fromJSDate(start));
          setMoveoutDate(DateTime.fromJSDate(end));
        }}
      />
    </>
  );
};

export default RequestVisitComponent;
